import React, { memo, useEffect, useState } from 'react'

import {
  useTwentyTwoMotion,
  useTwentyTwoViewport,
} from '../../components/twenty-two-motion'

import yoyoAvatar from './assets/images/yoyo-avatar.jpg'

const FEATURED_AUTHOR = '吉吉'
const FEATURED_AUTHOR_AVATAR = '/favicon.ico'
const YOYO_AVATAR_CARD_ID = 'card-069'
// Preserve the original curation: only these cards render their avatarUrl from cards.json.
const CARD_IDS_WITH_AVATAR = new Set([
  'card-005',
  'card-014',
  'card-019',
  'card-069',
  'card-027',
  'card-029',
  'card-039',
  'card-056',
  'card-074',
  'card-075',
  'card-089',
  'card-091',
  'card-095',
  'card-097',
  'card-098',
  'card-099',
  'card-105',
  'card-111',
  'card-114',
  'card-124',
  'card-138',
  'card-143',
])
const SHANGHAI_TIME_OFFSET = 8 * 60 * 60 * 1000

const CardAvatar = ({ card }) => {
  const displayName = card.myName && card.myName.trim()
  const initial = displayName ? Array.from(displayName)[0] : '·'
  const avatarUrl =
    displayName === FEATURED_AUTHOR
      ? FEATURED_AUTHOR_AVATAR
      : card.id === YOYO_AVATAR_CARD_ID
      ? yoyoAvatar
      : CARD_IDS_WITH_AVATAR.has(card.id)
      ? card.avatarUrl
      : undefined

  return (
    <span
      className={`TwentyTwoCardAvatar ${
        avatarUrl ? 'has-image' : 'is-fallback'
      }`}
      aria-hidden="true"
    >
      <span className="TwentyTwoCardAvatarFallback">{initial}</span>
      {avatarUrl ? (
        <img src={avatarUrl} alt="" loading="lazy" decoding="async" />
      ) : null}
    </span>
  )
}

const SpeechBubble = ({ children, gender, side }) => (
  <div
    className={`TwentyTwoBubble TwentyTwoBubble--${side} ${
      gender === '男生' ? 'is-male' : 'is-female'
    }`}
  >
    {children || '未留下描述'}
  </div>
)

const padTimePart = (value) => String(value).padStart(2, '0')

const getPreciseCardTime = (card) => {
  const timestamp = Number(card.timestamp)
  if (!Number.isFinite(timestamp)) {
    return {
      dateTime: card.time || undefined,
      label: card.time || '时间未记录',
    }
  }

  const instant = new Date(timestamp)
  const shanghaiTime = new Date(timestamp + SHANGHAI_TIME_OFFSET)
  const label =
    [
      shanghaiTime.getUTCFullYear(),
      padTimePart(shanghaiTime.getUTCMonth() + 1),
      padTimePart(shanghaiTime.getUTCDate()),
    ].join('-') +
    ` ${padTimePart(shanghaiTime.getUTCHours())}:${padTimePart(
      shanghaiTime.getUTCMinutes()
    )}`

  return { dateTime: instant.toISOString(), label }
}

const CardFavoriteIcon = () => (
  <svg
    className="TwentyTwoCardMetaIcon"
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M8 13.7 2.6 8.6A3.6 3.6 0 0 1 7.8 3.7L8 4l.2-.3a3.6 3.6 0 0 1 5.2 4.9Z" />
  </svg>
)

const CardCommentIcon = () => (
  <svg
    className="TwentyTwoCardMetaIcon"
    viewBox="0 0 16 16"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M2.2 2.5h11.6v8.1H6.4l-4.2 3V2.5Zm2 2v5.2l1.5-1.1h6.1V4.5H4.2Z" />
  </svg>
)

export const ConfessionCard = memo(
  ({ card, expanded, onToggle, duplicate = false, previewOnly = false }) => {
    const preciseTime = expanded ? getPreciseCardTime(card) : null
    const toggle = () => onToggle(card.id)
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggle()
      }
      if (event.key === 'Escape' && expanded) {
        event.preventDefault()
        toggle()
      }
    }

    return (
      <article
        className={`TwentyTwoCard ${card.vip ? 'is-vip' : ''} ${
          expanded ? 'is-expanded' : ''
        } ${previewOnly ? 'is-preview-only' : ''}`}
        tabIndex={duplicate || previewOnly ? '-1' : '0'}
        role={previewOnly ? undefined : 'button'}
        aria-expanded={previewOnly ? undefined : expanded}
        aria-hidden={duplicate || previewOnly ? 'true' : undefined}
        aria-label={
          previewOnly
            ? undefined
            : `${card.myName} 写给 ${card.taName} 的表白卡片，点击或按回车查看详情`
        }
        data-card-id={card.id}
        data-duplicate={duplicate ? 'true' : undefined}
        onClick={previewOnly ? undefined : toggle}
        onKeyDown={duplicate || previewOnly ? undefined : handleKeyDown}
      >
        <div className="TwentyTwoCardPeople">
          <span title={card.myName}>{card.myName}</span>
          <span className="TwentyTwoHeart" aria-hidden="true">
            ♥
          </span>
          <span title={card.taName}>{card.taName}</span>
        </div>
        <div className="TwentyTwoCardPreview">
          <CardAvatar card={card} />
          <p>{card.textarea}</p>
        </div>
        {expanded && !previewOnly && (
          <div className="TwentyTwoCardDetails">
            <div className="TwentyTwoCardTags">
              <span>{card.academy || '未知学院'}</span>
              <span>{card.grade || '未知年级'}</span>
            </div>
            <div className="TwentyTwoConversation">
              <SpeechBubble gender={card.myGender} side="left">
                {card.myDescription}
              </SpeechBubble>
              <SpeechBubble gender={card.taGender} side="right">
                {card.taDescription}
              </SpeechBubble>
            </div>
            <p className="TwentyTwoCardLetter">{card.textarea}</p>
            <div className="TwentyTwoCardMeta">
              <time dateTime={preciseTime.dateTime}>{preciseTime.label}</time>
              <span
                className="TwentyTwoCardStat"
                aria-label={`${card.starNum} 次收藏`}
              >
                <CardFavoriteIcon />
                <span>{card.starNum}</span>
              </span>
              <span
                className="TwentyTwoCardStat"
                aria-label={`${card.commentNum} 条评论`}
              >
                <CardCommentIcon />
                <span>{card.commentNum}</span>
              </span>
            </div>
          </div>
        )}
        <span className="TwentyTwoCardHint" aria-hidden="true">
          <span className="TwentyTwoCardHintIcon">{expanded ? '−' : '+'}</span>
          <span>{expanded ? '点击收起' : '点击完整展开'}</span>
        </span>
      </article>
    )
  }
)

export default () => null

export const CardFlow = memo(
  ({ cards, expandedCard, flowDuration, onToggle, rowIndex }) => {
    const { ref: flowRef, isNearViewport } = useTwentyTwoViewport('280px 0px')
    const { documentVisible, motionEnabled, loopEnabled } = useTwentyTwoMotion()
    const [loopPrepared, setLoopPrepared] = useState(false)
    useEffect(() => {
      if (loopEnabled && isNearViewport) setLoopPrepared(true)
    }, [loopEnabled, isNearViewport])
    // Keep prepared desktop copies on return visits to avoid remounting on scroll.
    // Touch, narrow and reduced-motion views never mount the duplicate set.
    const hasDuplicate = loopEnabled && loopPrepared
    const isActive = isNearViewport && documentVisible && motionEnabled
    const direction = rowIndex % 2 === 0 ? 'forward' : 'reverse'
    const rowExpanded = cards.some((card) => card.id === expandedCard)

    return (
      <div
        ref={flowRef}
        className={`TwentyTwoFlow TwentyTwoFlow--${direction} ${
          isActive ? 'is-active' : ''
        } ${hasDuplicate ? 'is-looping' : ''} ${
          rowExpanded ? 'has-expanded' : ''
        }`}
        data-card-count={cards.length}
        data-vip-count={cards.filter((card) => card.vip).length}
        style={{ '--flow-duration': `${flowDuration}s` }}
      >
        <div
          className="TwentyTwoFlowViewport"
          aria-label={`第 ${rowIndex + 1} 组表白卡片`}
        >
          <div className="TwentyTwoFlowTrack">
            {(hasDuplicate ? [0, 1] : [0]).map((copyIndex) => (
              <div
                className="TwentyTwoFlowSet"
                key={`flow-${rowIndex}-copy-${copyIndex}`}
                aria-hidden={copyIndex === 1 ? 'true' : undefined}
              >
                {cards.map((card) => (
                  <ConfessionCard
                    key={`${card.id}-copy-${copyIndex}`}
                    card={card}
                    duplicate={copyIndex === 1}
                    expanded={expandedCard === card.id}
                    onToggle={onToggle}
                  />
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
)
